import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { recordMount } from './lib/diag';
import { initTheme } from './lib/accent';
import { installPrefsSync, pullPrefs } from './lib/prefs';
import { initBgVideo } from './lib/bgVideo';
import './styles/global.css';

// Restore the user's chosen accent + success colours + theme before first
// paint so there's no flash of the defaults (Alex 2026-06-22).
initTheme();
// Ambient video background flag → [data-bg] on <html> before first paint.
initBgVideo();

// Server-prefs sync: intercept localStorage writes to debounce-PATCH synced
// keys, then pull the server blob (background) so settings follow the user
// across devices. localStorage stays the instant/offline source of truth.
installPrefsSync();
void pullPrefs();
// KaTeX stylesheet — required by rehype-katex output (Issue #18).
// Loaded once here so every <Markdown> site downstream picks it up
// without per-file imports.
import 'katex/dist/katex.min.css';

// Fix iOS Safari viewport height — always tracks visualViewport.height.
//
// Earlier revisions tried to "freeze" --vh while the soft keyboard was
// open, on the theory that re-flowing the container on every keystroke
// would jitter visibly. But the freeze caused a much worse bug
// (screenshot 110052): the container stays at full height while the
// visual viewport collapses, so iOS Safari scrolls the focused input
// up to make it visible — header and conversation slide out of view.
//
// Letting --vh follow visualViewport.height unconditionally means the
// container shrinks with the keyboard, the InputBar stays pinned at
// the bottom of the visible area, and the Safari URL-bar toggle (~5 %
// shrink) is too small to read as jitter.
function setVh() {
  const vv = window.visualViewport;
  // While pinch-zoomed (scale > 1), FREEZE the layout: don't recompute the height to the
  // shrunk visual viewport (that collapsed the container + floated the footer mid-screen,
  // #140). Frozen, pinch-zoom behaves like a normal webpage — everything scales uniformly,
  // you pan, the footer stays put. Resume tracking at scale ≈ 1 (the keyboard / URL-bar
  // case this was built for). Doing NOTHING while zoomed (not setting a wrong height) is
  // the key difference from the earlier failed attempt.
  if (vv && vv.scale > 1.01) return;
  // Der KLEINERE der beiden Werte: Meldet ein Browser den sichtbaren Viewport
  // nicht (mehr) verkleinert, während die Tastatur darüberliegt, wäre
  // vv.height zu groß — dann rettet innerHeight, sobald der Layout-Viewport
  // schrumpft (`interactive-widget=resizes-content` in index.html). Umgekehrt
  // ist im klassischen Modell vv.height der kleinere und richtige Wert.
  const vpHeight = Math.min(vv?.height || window.innerHeight, window.innerHeight);
  document.documentElement.style.setProperty('--vh', `${vpHeight * 0.01}px`);
  // Wie viel Höhe zieht die Browser-Oberfläche schon selbst ab? (Layout-
  // Viewport minus sichtbarer Viewport.) Das entscheidet, ob wir unten noch
  // Platz freihalten müssen — und ersetzt eine Annahme durch eine Messung:
  //
  // Früher galt „die Leiste ist in visualViewport.height bereits abgezogen,
  // also NICHT zusätzlich safe-area-inset-bottom addieren, sonst klafft eine
  // fette Lücke". Stimmt eine Browser-Version das um und legt ihre Leiste
  // ÜBER die Seite, ist der Wert 0 — und die Eingabezeile verschwindet
  // dahinter (Alex, 18.08., Handy: „unten abgeschnitten, das habe ich
  // eigentlich immer gesehen"). Mit dieser Zahl entscheidet das Stylesheet
  // selbst, statt sich auf eine Seite festzulegen.
  const chromeBottom = Math.max(0, window.innerHeight - (vv?.height ?? window.innerHeight) - (vv?.offsetTop ?? 0));
  document.documentElement.style.setProperty('--chrome-bottom', `${chromeBottom}px`);
}
setVh();
window.visualViewport?.addEventListener('resize', setVh);
// iOS Safari auto-scrolls window.scrollY > 0 when a focused input would
// otherwise sit behind the keyboard. With #root as position: fixed +
// --vh tracking the visual viewport, the app already sizes itself
// correctly; the scroll just leaves a phantom blank strip below #root.
// Force the document back to (0, 0) whenever the visual viewport
// scrolls so the app stays pinned to the top of the visible area.
window.visualViewport?.addEventListener('scroll', () => window.scrollTo(0, 0));
window.addEventListener('orientationchange', () => setTimeout(setVh, 100));

// Diagnostics: record this mount (count + gap-to-previous + nav type)
// so a reload cadence is visible in the logs (CloudV2-diag session).
recordMount();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
